import React, { useEffect, useMemo, useState } from "react";

import { RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Spin } from "antd";
import { toast } from "react-toastify";

import { fetchUsageCategoryTests } from "../../../../actions/usage";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import { TEST_TIERS } from "../constants";
import { buildTestFilters, mapUsageCategoryTestsToModal } from "../utils";

import { ReactComponent as FlowersIllustration } from "../../../../assets/images/subscriptions/Flowers.svg";
import microscopeIcon from "../../../../assets/images/subscriptions/microscope-line.svg";
import reactjsIcon from "../../../../assets/images/subscriptions/reactjs-line.svg";

import "./UsageMetricsTestsDrawer.css";

const CATEGORY_ICONS = {
  microbiology: <img src={microscopeIcon} alt="" aria-hidden />,
  chemistry: <img src={reactjsIcon} alt="" aria-hidden />,
};

export default function UsageMetricsTestsDrawer({ open, onClose, overlay = false }) {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setActiveFilter("All");
      return undefined;
    }

    const controller = new AbortController();
    let isMounted = true;

    const loadTests = async () => {
      setLoading(true);
      const response = await fetchUsageCategoryTests({
        status: 1,
        signal: controller.signal,
      });

      if (!isMounted || response.canceled) return;

      if (!response.success) {
        toast.error(response.message);
        setLoading(false);
        return;
      }

      setCategories(
        mapUsageCategoryTestsToModal(response.categories, {}, {
          iconClassPrefix: "UsageMetricsTestsDrawer__CategoryIcon",
        }),
      );
      setLoading(false);
    };

    loadTests();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [open]);

  const filters = useMemo(() => buildTestFilters(categories), [categories]);

  const visibleCategories = useMemo(() => {
    const query = search.trim().toLowerCase();

    return categories.map((category) => ({
      ...category,
      tests: category.tests.filter((test) => (
        (activeFilter === "All" || test.tier === activeFilter)
        && (!query || test.name.toLowerCase().includes(query))
      )),
    })).filter((category) => category.tests.length > 0);
  }, [activeFilter, categories, search]);

  return (
    <aside
      className={`UsageMetricsTestsDrawer${open ? " UsageMetricsTestsDrawer--open" : ""}${overlay ? " UsageMetricsTestsDrawer--overlay" : ""}`}
      aria-label="Tests in category"
      aria-hidden={!open}
    >
      <FlowersIllustration
        className="UsageMetricsTestsDrawer__Flower UsageMetricsTestsDrawer__Flower--topLeft"
        aria-hidden
      />
      <header className="UsageMetricsTestsDrawer__Header">
        <h2>Tests in category</h2>
        <Button
          type="text"
          icon={<RightOutlined />}
          className="UsageMetricsTestsDrawer__CloseButton"
          onClick={onClose}
          aria-label="Close tests drawer"
        />
      </header>

      <div className="UsageMetricsTestsDrawer__Controls">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          prefix={<SearchOutlined />}
          placeholder="Search tests..."
          allowClear
          className="UsageMetricsTestsDrawer__Search"
        />
        <StyledScrollbar
          className="UsageMetricsTestsDrawer__FiltersScroll"
          noScrollY
          autoHide="x"
          disableTrackXWidthCompensation
          disableTracksWidthCompensation
        >
          <div className="UsageMetricsTestsDrawer__Filters">
            {filters.map((filter, index) => (
              <React.Fragment key={filter.key}>
                {index === 1 && (
                  <span className="UsageMetricsTestsDrawer__FilterDivider" aria-hidden />
                )}
                <button
                  type="button"
                  onClick={() => setActiveFilter(filter.key)}
                  className={`UsageMetricsTestsDrawer__Filter${
                    activeFilter === filter.key ? " UsageMetricsTestsDrawer__Filter--active" : ""
                  }`}
                >
                  {filter.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </StyledScrollbar>
      </div>

      {loading ? (
        <div className="UsageMetricsTestsDrawer__Loading">
          <Spin size="large" />
        </div>
      ) : (
        <StyledScrollbar
          className="UsageMetricsTestsDrawer__Scroll"
          noScrollX
          autoHide
          disableTrackYWidthCompensation
          disableTracksWidthCompensation
        >
          <div className="UsageMetricsTestsDrawer__Categories">
            {visibleCategories.map((category) => (
              <section className="UsageMetricsTestsDrawer__Category" key={category.key}>
                <h3 className="UsageMetricsTestsDrawer__CategoryTitle">
                  <span className={`UsageMetricsTestsDrawer__CategoryIcon ${category.iconClass}`}>
                    {CATEGORY_ICONS[category.key]}
                  </span>
                  {category.name}
                </h3>

                {TEST_TIERS.map((tier) => {
                  const tests = category.tests.filter((test) => test.tier === tier);
                  if (!tests.length) return null;

                  return (
                    <div className="UsageMetricsTestsDrawer__Tier" key={tier}>
                      <h4>{tier}</h4>
                      <StyledScrollbar
                        className="UsageMetricsTestsDrawer__TierScroll"
                        noScrollX
                        autoHide
                        disableTrackYWidthCompensation
                        disableTracksWidthCompensation
                      >
                        <ul>
                          {tests.map((test) => (
                            <li key={test.id}>{test.name}</li>
                          ))}
                        </ul>
                      </StyledScrollbar>
                    </div>
                  );
                })}
              </section>
            ))}

            {!visibleCategories.length && (
              <p className="UsageMetricsTestsDrawer__Empty">No tests found.</p>
            )}
          </div>
        </StyledScrollbar>
      )}
    </aside>
  );
}
